<template>
  <div>
    <!-- Content Table Container -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div 
          v-for="item in items" 
          :key="item.id" 
          class="p-4 hover:bg-gray-50 transition-colors"
          :class="{'bg-red-50/50': item.status === 'cancelled'}"
        >
          <div class="space-y-3">
            <div class="flex justify-between items-start gap-2">
              <div class="flex items-center gap-2">
                <span 
                  :class="item.type === 'shopping' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'"
                  class="px-2 py-0.5 rounded text-xs font-bold shrink-0"
                >
                  {{ item.type === 'shopping' ? 'Compra' : 'Gasto' }}
                </span>
                <span class="font-bold text-gray-900 text-sm">#{{ item.raw_id }}</span>
              </div>

              <span 
                :class="getStatusClass(item.status)"
                class="px-2 py-0.5 rounded text-xs font-bold shrink-0"
              >
                {{ item.status_label }}
              </span>
            </div>
            
            <div class="text-sm text-gray-600 space-y-1">
              <p class="font-medium text-gray-800">{{ item.title }}</p>
              <p class="text-xs text-gray-500 flex items-center gap-1.5">
                <i class="fa fa-building text-gray-400 text-xs" />
                <span>{{ item.provider_name }}</span>
              </p>
              <div class="flex items-center justify-between text-xs text-gray-400 pt-1">
                <span>{{ formatDate(item.date) }}</span>
                <span 
                  :class="item.affects_inventory ? 'text-emerald-600 font-medium' : 'text-gray-400'"
                  class="flex items-center gap-1"
                >
                  <i :class="item.affects_inventory ? 'fa fa-check-circle' : 'fa fa-minus-circle'" />
                  {{ item.affects_inventory ? '+ Stock' : 'Sin stock' }}
                </span>
              </div>
            </div>

            <div class="flex justify-between items-center pt-2 border-t border-gray-100 mt-2">
              <span class="text-xs text-gray-500">
                {{ item.method }}
              </span>
              <span class="font-bold text-gray-900 text-base">{{ formatUSD(item.amount) }}</span>
            </div>

            <!-- Mobile action link -->
            <div class="pt-2 flex justify-end gap-3 border-t border-gray-50">
              <Link 
                :href="item.view_url" 
                class="font-medium text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
              >
                <i class="fa fa-eye" />
                Ver / Gestionar
              </Link>
              <button
                v-if="item.type === 'spent'"
                type="button"
                class="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1 cursor-pointer"
                @click="$emit('delete-spent', item.raw_id)"
              >
                <i class="fa fa-trash-alt" />
                Eliminar
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile Empty State -->
        <div v-if="!items || items.length === 0" class="p-8 text-center text-gray-400">
          <i class="fa fa-receipt text-4xl mb-3 text-gray-300" />
          <h3 class="text-base font-bold text-gray-800">No hay movimientos registrados</h3>
          <p class="text-xs text-gray-500 mt-1">Las compras y gastos aparecerán aquí.</p>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap text-left">
          <thead>
            <tr class="bg-gray-50/70 border-b border-gray-100">
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Tipo & ID</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Concepto / Referencia</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Proveedor</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Stock</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Fecha</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Estado</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Monto</th>
              <th class="py-3.5 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Acción</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="item in items" 
              :key="item.id" 
              class="group hover:bg-gray-50 transition-colors"
              :class="{'bg-red-50/40 hover:bg-red-50/70': item.status === 'cancelled'}"
            >
              <!-- Tipo & ID -->
              <td class="px-6 py-4">
                <div class="flex items-center gap-2">
                  <span 
                    :class="item.type === 'shopping' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'"
                    class="px-2.5 py-1 rounded text-xs font-bold inline-block"
                  >
                    {{ item.type === 'shopping' ? 'Compra' : 'Gasto' }}
                  </span>
                  <Link :href="item.view_url" class="font-medium text-blue-600 hover:text-blue-700 text-sm">
                    #{{ item.raw_id }}
                  </Link>
                </div>
              </td>

              <!-- Concepto / Referencia -->
              <td class="px-6 py-4">
                <Link :href="item.view_url" class="block">
                  <p class="font-medium text-gray-900 text-sm hover:text-blue-600 transition-colors">{{ item.title }}</p>
                  <p class="text-xs text-gray-400">{{ item.category }}</p>
                </Link>
              </td>
              
              <!-- Proveedor -->
              <td class="px-6 py-4 text-gray-800 font-medium text-sm">
                <div v-if="item.has_provider" class="flex items-center gap-1.5">
                  <i class="fa fa-building text-gray-400 text-xs" />
                  <span>{{ item.provider_name }}</span>
                </div>
                <span v-else class="text-xs text-gray-400 font-normal italic">
                  Sin proveedor (Interno)
                </span>
              </td>

              <!-- Impacto Inventario -->
              <td class="px-6 py-4">
                <span 
                  v-if="item.affects_inventory"
                  class="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
                >
                  <i class="fa fa-check-circle text-emerald-500 text-[10px]" />
                  + Suma stock
                </span>
                <span 
                  v-else
                  class="text-xs text-gray-400"
                >
                  No afecta
                </span>
              </td>

              <!-- Fecha -->
              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ formatDate(item.date) }}
              </td>

              <!-- Estado -->
              <td class="px-6 py-4">
                <span 
                  :class="[
                    'px-2.5 py-1 rounded text-xs font-bold inline-block',
                    getStatusClass(item.status)
                  ]"
                >
                  {{ item.status_label }}
                </span>
              </td>

              <!-- Total / Monto -->
              <td class="px-6 py-4 text-right font-bold text-gray-900 text-sm">
                <div>{{ formatUSD(item.amount) }}</div>
                <div v-if="item.currency === 'BS' && item.exchange_rate" class="text-[11px] text-gray-400 font-normal font-mono">
                  Bs. {{ (item.amount * item.exchange_rate).toLocaleString('es-VE', { minimumFractionDigits: 2 }) }}
                </div>
              </td>

              <!-- Acciones -->
              <td class="px-6 py-4 text-right">
                <div class="flex items-center justify-end gap-3">
                  <Link 
                    :href="item.view_url"
                    class="font-medium text-blue-600 hover:text-blue-700 text-sm"
                    title="Ver detalle"
                  >
                    Ver
                  </Link>

                  <button
                    v-if="item.type === 'spent'"
                    type="button"
                    class="text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                    title="Eliminar gasto"
                    @click="$emit('delete-spent', item.raw_id)"
                  >
                    <i class="fa fa-trash-alt text-xs" />
                  </button>
                </div>
              </td>
            </tr>

            <!-- Desktop Empty State -->
            <tr v-if="!items || items.length === 0">
              <td colspan="8" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-receipt text-2xl opacity-50" />
                  </div>
                  <h3 class="text-base font-bold text-gray-900 mb-1">No hay movimientos registrados</h3>
                  <p class="text-gray-500 text-sm max-w-sm mb-4">
                    Las compras de mercancía y gastos registrados aparecerán aquí.
                  </p>
                  <div class="flex items-center gap-3">
                    <button 
                      type="button"
                      class="px-4 py-2 bg-white border border-gray-300 hover:border-gray-400 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-xs shadow-sm transition-colors cursor-pointer"
                      @click="$emit('open-quick-spent')"
                    >
                      + Nuevo Gasto
                    </button>
                    <Link 
                      href="/shoppings/create?type=shopping"
                      class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium text-xs shadow-sm transition-colors"
                    >
                      + Crear Compra
                    </Link>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="paginationLinks?.length" class="mt-6 flex justify-center">
      <pagination :links="paginationLinks" />
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Pagination from '@/Shared/Pagination.vue'

export default {
  name: 'ShoppingTable',

  components: {
    Link,
    Pagination,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    paginationLinks: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['open-quick-spent', 'delete-spent'],

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const parts = dateStr.split('T')[0].split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return new Date(dateStr).toLocaleDateString()
    },

    getStatusClass(status) {
      switch(status) {
      case 'completed': 
      case 'paid': 
        return 'bg-green-100 text-green-800'
      case 'pending': 
        return 'bg-amber-100 text-amber-800'
      case 'cancelled': 
        return 'bg-red-100 text-red-800'
      default: 
        return 'bg-gray-100 text-gray-800'
      }
    },

    formatUSD(value) {
      const val = parseFloat(value) || 0
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(val)
    },
  },
}
</script>
